import { useId } from "react";

import { DiagramFrame } from "@/app/features/diagrams/components/shared/DiagramFrame";
import { DiagramMarkers } from "@/app/features/diagrams/components/shared/DiagramMarkers";
import { DiagramNode } from "@/app/features/diagrams/components/shared/DiagramNode";
import { DiagramDecisionGate } from "@/app/features/diagrams/components/shared/DiagramDecisionGate";

import "@/app/features/diagrams/functions/s1000d/brexTopologySpec";

export function IssueBrexIntakeGateVisual() {
  const titleId = useId();
  const descId = useId();

  return (
    <DiagramFrame
      width={720}
      height={520}
      title="S1000D Issue & BREX Intake Verification Pipeline"
      description="Verification pipeline showing intake of multi-issue S1000D corpora through three sequential gates: Schema Validation (XSD), Business Rules (BREX XPath), and Reference Graph Integrity, with quarantine triage."
      titleId={titleId}
      descId={descId}
      viewBox="0 0 720 520"
      frame={{
        kind: "boundary",
        label: "S1000D INGESTION & VERIFICATION GATES",
        x: 18,
        y: 18,
        width: 684,
        height: 484,
      }}
    >
      <DiagramMarkers prefix="brex-" />

      {/* RAW ARCHIVE */}
      <DiagramNode
        x={100}
        y={40}
        width={320}
        height={46}
        eyebrow="LEGACY CSDB EXPORT"
        label="Multi-Issue Source Archive"
        sublabel="Issues 2.3 · 3.0 · 4.1 · 5.0"
        variant="capsule"
      />

      <path
        d="M 260 86 L 260 110"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
        markerEnd="url(#brex-arrow-neutral)"
      />

      {/* ISSUE FINGERPRINTING */}
      <DiagramNode
        x={100}
        y={110}
        width={320}
        height={46}
        label="Issue Detector & Normalizer"
        sublabel="Inspect DTD Declaration & Target XSD"
        variant="standard"
      />

      <path
        d="M 260 156 L 260 178"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
      />

      {/* GATE 1: SCHEMA VALIDATION (Centered at X=260: 222 + 38 = 260) */}
      <DiagramDecisionGate x={222} y={178} label="XSD?" />

      {/* GATE 1 FAIL PATH */}
      <path
        d="M 298 198 L 460 198"
        fill="none"
        stroke="var(--ga-diagram-danger, #dc2626)"
        strokeWidth="1.5"
        strokeDasharray="4 3"
        markerEnd="url(#brex-arrow-fallback)"
      />
      <DiagramNode
        x={460}
        y={175}
        width={215}
        height={46}
        label="Schema Quarantine"
        sublabel="DTD Casing Drift & Syntax Errors"
        variant="fallback"
      />

      {/* GATE 1 PASS PATH */}
      <path
        d="M 260 218 L 260 252"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
      />

      {/* GATE 2: BREX BUSINESS RULES */}
      <DiagramDecisionGate x={222} y={252} label="BREX?" />

      {/* GATE 2 FAIL PATH */}
      <path
        d="M 298 272 L 460 272"
        fill="none"
        stroke="var(--ga-diagram-danger, #dc2626)"
        strokeWidth="1.5"
        strokeDasharray="4 3"
        markerEnd="url(#brex-arrow-fallback)"
      />
      <DiagramNode
        x={460}
        y={249}
        width={215}
        height={46}
        label="BREX Violation Triage"
        sublabel="Prohibited Elements & Styles"
        variant="fallback"
      />

      {/* GATE 2 PASS PATH */}
      <path
        d="M 260 292 L 260 326"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        opacity="0.45"
      />

      {/* GATE 3: REFERENCE & CIR INTEGRITY */}
      <DiagramDecisionGate x={222} y={326} label="REFS?" />

      {/* GATE 3 FAIL PATH */}
      <path
        d="M 298 346 L 460 346"
        fill="none"
        stroke="var(--ga-diagram-danger, #dc2626)"
        strokeWidth="1.5"
        strokeDasharray="4 3"
        markerEnd="url(#brex-arrow-fallback)"
      />
      <DiagramNode
        x={460}
        y={323}
        width={215}
        height={46}
        label="Dangling Reference Ledger"
        sublabel="Missing CIR & Target DM Records"
        variant="fallback"
      />

      {/* GATE 3 PASS PATH */}
      <path
        d="M 260 366 L 260 408"
        fill="none"
        stroke="var(--ga-diagram-success, #15803d)"
        strokeWidth="1.8"
        markerEnd="url(#brex-arrow-green)"
      />

      {/* VERIFIED CANONICAL INGEST */}
      <DiagramNode
        x={70}
        y={410}
        width={380}
        height={58}
        eyebrow="VERIFIED CANONICAL BASELINE"
        label="Canonical Publication Model Intake"
        sublabel="Multi-Issue Schemas Harmonized · Zero Defect Corpus"
        variant="success"
      />
    </DiagramFrame>
  );
}
